'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { toCurrency, decodeEntities } from '@/app/_lib/services/ClientUtil';
import { saveProductConfiguration, getProductConfiguration } from '@/app/_lib/services/RecentlyViewedService';

/**
 * useProduct - Central hook for product page state management
 *
 * Manages all product configuration state:
 * - Selected options and swatches
 * - Derived pricing (retail and trade)
 * - Variant matching
 * - Lead time calculation
 * - Image list (including variant images)
 *
 * @param {Object} initialProduct - The sub_product object from server
 * @returns {Object} Product state and operations
 */
export function useProduct(initialProduct) {
  const {
    status: sessionStatus
  } = useSession();
  const isAuthenticated = sessionStatus === 'authenticated';

  // =========================================================================
  // CORE STATE
  // =========================================================================

  const [selectedOptions, setSelectedOptions] = useState({
    options: {},
    // { [selectionId]: optionObject }
    swatches: {} // { [swatchSetId]: swatchObject }
  });
  const [currentVariant, setCurrentVariant] = useState(null);
  const [tradePricing, setTradePricing] = useState(null);
  const [shippingMessage, setShippingMessage] = useState(initialProduct.shipping?.product_page_message);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [variantImages, setVariantImages] = useState([]);
  const [leadTime, setLeadTime] = useState(initialProduct.lead_time || null);
  const isInitialLoadRef = useRef(true);

  // =========================================================================
  // DERIVED: Option Code (Internal SKU)
  // =========================================================================

  // Build option code: {sub_product_id}-{option_ids by selection_id}-{swatch_ids by swatch_set_id}
  const optionCode = useMemo(() => {
    const parts = [initialProduct.sub_product_id];

    // Options sorted by selection_id (ascending)
    const sortedSelectionIds = Object.keys(selectedOptions.options).map(Number).sort((a, b) => a - b);
    for (const selectionId of sortedSelectionIds) {
      const option = selectedOptions.options[selectionId];
      if (option?.option_id) {
        parts.push(option.option_id);
      }
    }

    // Swatches sorted by swatch_set_id (ascending)
    const sortedSwatchSetIds = Object.keys(selectedOptions.swatches).map(Number).sort((a_0, b_0) => a_0 - b_0);
    for (const swatchSetId of sortedSwatchSetIds) {
      const swatch = selectedOptions.swatches[swatchSetId];
      if (swatch?.swatch_id) {
        parts.push(swatch.swatch_id);
      }
    }
    return parts.join('-');
  }, [initialProduct.sub_product_id, selectedOptions]);

  // =========================================================================
  // DERIVED: Completion State
  // =========================================================================

  const {
    isComplete,
    selectionErrors,
    swatchSetErrors
  } = useMemo(() => {
    const selections = initialProduct.selections || [];
    const swatchSets = initialProduct.swatchSets || [];
    const selectedSelectionIds = new Set(Object.keys(selectedOptions.options).map(Number));
    const selectedSwatchSetIds = new Set(Object.keys(selectedOptions.swatches).map(Number));
    const selectionErrs = {};
    const swatchSetErrs = {};
    let complete = true;

    // Check all required selections are made
    for (const selection of selections) {
      if (!selectedSelectionIds.has(selection.selection_id)) {
        selectionErrs[selection.selection_id] = 'Please select';
        complete = false;
      }
    }

    // Check all required swatch sets are made
    for (const swatchSet of swatchSets) {
      if (!selectedSwatchSetIds.has(swatchSet.swatch_set_id)) {
        swatchSetErrs[swatchSet.swatch_set_id] = 'Please select';
        complete = false;
      }
    }
    return {
      isComplete: complete,
      selectionErrors: selectionErrs,
      swatchSetErrors: swatchSetErrs
    };
  }, [initialProduct.selections, initialProduct.swatchSets, selectedOptions]);

  // =========================================================================
  // DERIVED: Availability
  // =========================================================================

  const availability = useMemo(() => {
    const result = {
      canPurchase: true,
      messages: [],
      productDisabled: false,
      hasBackorderedSelection: false,
      hasHoldSelection: false
    };

    // Check product-level disable
    if (initialProduct.disable_purchase) {
      result.canPurchase = false;
      result.productDisabled = true;
      // Only add to messages if not already shown in lead time section
      if (!initialProduct.custom_backorder_message) {
        if (initialProduct.backorder_message) {
          result.messages.push(initialProduct.backorder_message);
        } else if (initialProduct.button_replacement_text) {
          result.messages.push(initialProduct.button_replacement_text);
        }
      }
      return result;
    }

    // Check selected options availability
    for (const option_0 of Object.values(selectedOptions.options)) {
      if (option_0?.availability) {
        if (!option_0.availability.canPurchase) {
          result.canPurchase = false;
          result.hasHoldSelection = true;
        }
        if (option_0.availability.isBackordered) {
          result.hasBackorderedSelection = true;
        }
        if (option_0.availability.isOnHold) {
          result.hasHoldSelection = true;
        }
        if (option_0.availability.message) {
          result.messages.push({
            type: 'option',
            name: option_0.option_name,
            label: option_0.availability.label,
            dateMessage: option_0.availability.dateMessage,
            message: option_0.availability.message,
            canPurchase: option_0.availability.canPurchase
          });
        }
      }
    }

    // Check selected swatches availability
    for (const swatch_0 of Object.values(selectedOptions.swatches)) {
      if (swatch_0?.availability) {
        if (!swatch_0.availability.canPurchase) {
          result.canPurchase = false;
          result.hasHoldSelection = true;
        }
        if (swatch_0.availability.isBackordered) {
          result.hasBackorderedSelection = true;
        }
        if (swatch_0.availability.isOnHold) {
          result.hasHoldSelection = true;
        }
        if (swatch_0.availability.message) {
          result.messages.push({
            type: 'swatch',
            name: swatch_0.swatch_name,
            label: swatch_0.availability.label,
            dateMessage: swatch_0.availability.dateMessage,
            message: swatch_0.availability.message,
            canPurchase: swatch_0.availability.canPurchase
          });
        }
      }
    }
    return result;
  }, [initialProduct.disable_purchase, initialProduct.backorder_message, initialProduct.button_replacement_text, selectedOptions]);

  // =========================================================================
  // DERIVED: Pricing
  // =========================================================================

  const pricing = useMemo(() => {
    const basePrice = parseFloat(initialProduct.retail_price) || 0;
    const originalBasePrice = parseFloat(initialProduct.original_price) || basePrice;

    // Calculate upcharges from selected options
    let optionsCost = 0;
    let originalOptionsCost = 0;
    for (const option_1 of Object.values(selectedOptions.options)) {
      if (option_1?.option_price > 0) {
        optionsCost += parseFloat(option_1.option_price);
      }
      if (option_1?.original_option_price > 0) {
        originalOptionsCost += parseFloat(option_1.original_option_price);
      } else if (option_1?.option_price > 0) {
        originalOptionsCost += parseFloat(option_1.option_price);
      }
    }

    // Calculate upcharges from selected swatches
    for (const swatch_1 of Object.values(selectedOptions.swatches)) {
      if (swatch_1?.effective_price > 0) {
        optionsCost += swatch_1.effective_price;
      }
      if (swatch_1?.original_effective_price > 0) {
        originalOptionsCost += swatch_1.original_effective_price;
      } else if (swatch_1?.effective_price > 0) {
        originalOptionsCost += swatch_1.effective_price;
      }
    }
    const totalPrice = basePrice + optionsCost;
    const originalTotalPrice = originalBasePrice + originalOptionsCost;

    // Price range (from product data)
    const lowestPrice = parseFloat(initialProduct.lowest_price) || 0;
    const highestPrice = parseFloat(initialProduct.highest_price) || 0;
    const hasPriceRange = lowestPrice > 0 && highestPrice > 0 && highestPrice > lowestPrice;
    const originalLowestPrice = parseFloat(initialProduct.original_lowest_price) || 0;
    const originalHighestPrice = parseFloat(initialProduct.original_highest_price) || 0;
    return {
      // Current calculated price (when selections made)
      totalPrice,
      originalTotalPrice: originalTotalPrice > totalPrice ? originalTotalPrice : null,
      // Upcharges breakdown
      optionsCost,
      originalOptionsCost: originalOptionsCost > optionsCost ? originalOptionsCost : null,
      // Price range (shown when incomplete)
      hasPriceRange,
      priceRange: hasPriceRange ? {
        min: lowestPrice,
        max: highestPrice
      } : null,
      originalPriceRange: hasPriceRange && originalLowestPrice > 0 ? {
        min: originalLowestPrice,
        max: originalHighestPrice
      } : null,
      // Sale info
      onSale: initialProduct.on_sale == 1,
      discountPercentage: initialProduct.discount_percentage || 0
    };
  }, [initialProduct, selectedOptions]);

  // =========================================================================
  // DERIVED: Trade Pricing
  // =========================================================================

  const tradePricingDerived = useMemo(() => {
    if (!tradePricing?.trade_account) {
      return {
        isTradeAccount: false,
        tradePrice: null,
        tradePriceRange: null,
        tradeDiscountRate: null,
        tradeDiscountOverridden: false
      };
    }
    const discountRate = parseFloat(tradePricing.trade_discount_rate) || 0;
    const tradePrice = parseFloat(tradePricing.trade_price) || 0;

    // Check if sale price overrides trade discount (discount=0 means sale was better)
    const tradeDiscountOverridden = pricing.onSale && parseFloat(tradePricing.discount) === 0;

    // Calculate trade price range
    let tradePriceRange = null;
    if (pricing.hasPriceRange && pricing.priceRange) {
      if (tradeDiscountOverridden) {
        // Sale overrides trade - trade price equals retail (sale) price
        tradePriceRange = {
          min: pricing.priceRange.min,
          max: pricing.priceRange.max
        };
      } else {
        // Trade discount applies on top of (or instead of) sale
        tradePriceRange = {
          min: pricing.priceRange.min * (1 - discountRate),
          max: pricing.priceRange.max * (1 - discountRate)
        };
      }
    }
    return {
      isTradeAccount: true,
      tradePrice: isComplete ? tradePrice : null,
      tradePriceRange,
      tradeDiscountRate: discountRate,
      tradeDiscountOverridden
    };
  }, [tradePricing, pricing, isComplete]);

  // optionMessages removed - availability.messages handles backorder/production hold display

  // =========================================================================
  // EFFECT: Calculate lead time (server action)
  // =========================================================================

  const hasSelectionsOrSwatches = initialProduct.selections?.length > 0 || initialProduct.swatchSets?.length > 0;
  useEffect(() => {
    // Products without options/swatches already have lead_time from data layer
    if (!hasSelectionsOrSwatches) return;

    // Wait for config to load and selections to be complete
    if (!configLoaded || !isComplete) return;
    let cancelled = false;
    const fetchLeadTime = async () => {
      const response = await fetch('/api/product/lead-time', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sku: optionCode,
          options: selectedOptions.options,
          swatches: selectedOptions.swatches,
          product_min_handling_time: initialProduct.min_handling_time,
          product_max_handling_time: initialProduct.max_handling_time,
          default_min_handling_time: initialProduct.default_min_handling_time,
          default_max_handling_time: initialProduct.default_max_handling_time,
          default_quick_ship_min_handling_time: initialProduct.default_quick_ship_min_handling_time,
          default_quick_ship_max_handling_time: initialProduct.default_quick_ship_max_handling_time
        })
      });
      const result_0 = await response.json();
      if (!cancelled) {
        setLeadTime(result_0.leadTime);
      }
    };
    fetchLeadTime();
    return () => {
      cancelled = true;
    };
  }, [optionCode, selectedOptions, isComplete, configLoaded, initialProduct, hasSelectionsOrSwatches]);

  // =========================================================================
  // EFFECT: Accumulate variant images
  // =========================================================================

  useEffect(() => {
    if (currentVariant?.file_name) {
      setVariantImages(prev => {
        // Check if this variant's image is already in the list
        const alreadyExists = prev.some(img => img.variant_id == currentVariant.variant_id);
        if (alreadyExists) return prev;

        // Add the new variant image
        const firstImage = initialProduct.images?.[0];
        return [...prev, {
          variant_id: currentVariant.variant_id,
          file_name: currentVariant.file_name,
          width: firstImage?.width,
          height: firstImage?.height,
          alt_attribute: decodeEntities(initialProduct.sub_product_name),
          caption: '',
          label_attribute: ''
        }];
      });
    }
  }, [currentVariant, initialProduct.images, initialProduct.sub_product_name]);

  // =========================================================================
  // DERIVED: Images
  // =========================================================================

  const images = useMemo(() => {
    const baseImages = [...(initialProduct.images || [])];

    // Add all accumulated variant images
    for (const variantImg of variantImages) {
      const alreadyExists_0 = baseImages.some(img_0 => img_0.variant_id && img_0.variant_id == variantImg.variant_id);
      if (!alreadyExists_0) {
        baseImages.push(variantImg);
      }
    }
    return baseImages;
  }, [initialProduct.images, variantImages]);

  // =========================================================================
  // DERIVED: Display SKU (MFG SKU from variant)
  // =========================================================================

  const displaySku = currentVariant?.mpn || null;

  // =========================================================================
  // EFFECTS: Load configuration from URL variant_id or saved config
  // =========================================================================

  useEffect(() => {
    const loadConfig = async () => {
      // Check URL for variant_id first (takes priority over saved config)
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant_id');
      if (variantId) {
        const variantResponse = await fetch(`/api/product/variant?id=${variantId}`);
        const variant = await variantResponse.json();
        if (variant?.options_json) {
          try {
            const optionsFromVariant = JSON.parse(variant.options_json);
            if (optionsFromVariant && Array.isArray(optionsFromVariant)) {
              const validatedOptions = {};
              const validatedSwatches = {};
              for (const item of optionsFromVariant) {
                if (item.swatch_id) {
                  // Find the swatch in swatchSets
                  const swatchSet_0 = initialProduct.swatchSets?.find(s => s.swatch_set_id == item.swatch_set_id);
                  if (swatchSet_0) {
                    for (const line of swatchSet_0.swatchLines || []) {
                      const fullSwatch = line.swatches?.find(s_0 => s_0.swatch_id == item.swatch_id);
                      if (fullSwatch) {
                        validatedSwatches[item.swatch_set_id] = fullSwatch;
                        break;
                      }
                    }
                  }
                } else if (item.option_id) {
                  // Find the option in selections
                  const selection_0 = initialProduct.selections?.find(s_1 => s_1.selection_id == item.selection_id);
                  const fullOption = selection_0?.options?.find(o => o.option_id == item.option_id);
                  if (fullOption) {
                    validatedOptions[item.selection_id] = fullOption;
                  }
                }
              }
              if (Object.keys(validatedOptions).length > 0 || Object.keys(validatedSwatches).length > 0) {
                setSelectedOptions({
                  options: validatedOptions,
                  swatches: validatedSwatches
                });
                setConfigLoaded(true);
                isInitialLoadRef.current = false;
                return;
              }
            }
          } catch (e) {
            // Invalid JSON, fall through to saved config
          }
        }
      }

      // Fall back to saved configuration from localStorage
      const savedConfig = getProductConfiguration(initialProduct.sub_product_id);
      if (savedConfig) {
        const {
          options,
          swatches
        } = savedConfig;

        // Validate against current product structure
        const validSelectionIds = new Set((initialProduct.selections || []).map(s_2 => s_2.selection_id));
        const validSwatchSetIds = new Set((initialProduct.swatchSets || []).map(s_3 => s_3.swatch_set_id));

        // Look up full option objects
        const validatedOptions_0 = {};
        if (options) {
          for (const [selectionId_0, optionId] of Object.entries(options)) {
            if (optionId != null && validSelectionIds.has(parseInt(selectionId_0))) {
              const selection_1 = initialProduct.selections.find(s_4 => s_4.selection_id == selectionId_0);
              const fullOption_0 = selection_1?.options?.find(o_0 => o_0.option_id == optionId);
              if (fullOption_0) {
                validatedOptions_0[selectionId_0] = fullOption_0;
              }
            }
          }
        }

        // Look up full swatch objects
        const validatedSwatches_0 = {};
        if (swatches) {
          for (const [swatchSetId_0, swatchId] of Object.entries(swatches)) {
            if (swatchId != null && validSwatchSetIds.has(parseInt(swatchSetId_0))) {
              const swatchSet_1 = initialProduct.swatchSets.find(s_5 => s_5.swatch_set_id == swatchSetId_0);
              let fullSwatch_0 = null;
              for (const line_0 of swatchSet_1?.swatchLines || []) {
                fullSwatch_0 = line_0.swatches?.find(s_6 => s_6.swatch_id == swatchId);
                if (fullSwatch_0) break;
              }
              if (fullSwatch_0) {
                validatedSwatches_0[swatchSetId_0] = fullSwatch_0;
              }
            }
          }
        }
        if (Object.keys(validatedOptions_0).length > 0 || Object.keys(validatedSwatches_0).length > 0) {
          setSelectedOptions({
            options: validatedOptions_0,
            swatches: validatedSwatches_0
          });
        }
      }
      setConfigLoaded(true);
      isInitialLoadRef.current = false;
    };
    loadConfig();
  }, [initialProduct]);

  // =========================================================================
  // EFFECTS: Look up variant when options complete
  // =========================================================================

  useEffect(() => {
    if (!isComplete || !configLoaded) return;

    // Look up variant from server-rendered variants by option_code
    const variants = initialProduct.variants || [];
    const matchingVariant = variants.find(v => v.option_code === optionCode);
    if (matchingVariant) {
      setCurrentVariant(matchingVariant);
    } else {
      setCurrentVariant(null);
    }
  }, [isComplete, optionCode, initialProduct.variants, configLoaded]);

  // =========================================================================
  // EFFECTS: Fetch trade pricing
  // =========================================================================

  useEffect(() => {
    if (sessionStatus === 'loading') return;
    if (!isAuthenticated) {
      setTradePricing(null);
      return;
    }
    let cancelled_0 = false;
    const fetchTradePricing = async () => {
      const response_0 = await fetch('/api/product/trade-pricing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sub_product_id: initialProduct.sub_product_id,
          options: Object.values(selectedOptions.options),
          swatches: Object.values(selectedOptions.swatches)
        })
      });
      const data = await response_0.json();
      if (cancelled_0) return;
      if (data.success) {
        setTradePricing(data);
      }
    };
    fetchTradePricing();
    return () => {
      cancelled_0 = true;
    };
  }, [sessionStatus, isAuthenticated, initialProduct.sub_product_id, selectedOptions]);

  // =========================================================================
  // EFFECTS: Fetch shipping
  // =========================================================================

  useEffect(() => {
    if (!configLoaded) return;
    let cancelled_1 = false;
    const fetchShipping = async () => {
      const response_1 = await fetch('/api/product/shipping', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_total: pricing.totalPrice,
          supplier_id: initialProduct.supplier_id,
          shipping_rate: initialProduct.shipping_rate,
          shipping_calculation_method: initialProduct.shipping_calculation_method,
          quantity: 1,
          // Base shipping for quantity 1
          shipping_cost: initialProduct.shipping_cost,
          shipping_required: initialProduct.shipping_required
        })
      });
      const data_0 = await response_1.json();
      if (cancelled_1) return;
      if (data_0.product_shipping?.product_page_message) {
        setShippingMessage(data_0.product_shipping.product_page_message);
      }
    };
    fetchShipping();
    return () => {
      cancelled_1 = true;
    };
  }, [pricing.totalPrice, initialProduct, configLoaded]);

  // =========================================================================
  // EFFECTS: Save configuration on change
  // =========================================================================

  useEffect(() => {
    if (!configLoaded || isInitialLoadRef.current) return;

    // Extract only IDs for storage
    const optionIds = {};
    for (const [selectionId_1, opt] of Object.entries(selectedOptions.options)) {
      if (opt != null) {
        optionIds[selectionId_1] = opt.option_id;
      }
    }
    const swatchIds = {};
    for (const [swatchSetId_1, swatch_2] of Object.entries(selectedOptions.swatches)) {
      if (swatch_2 != null) {
        swatchIds[swatchSetId_1] = swatch_2.swatch_id;
      }
    }
    saveProductConfiguration(initialProduct.sub_product_id, optionIds, swatchIds);
  }, [selectedOptions, initialProduct.sub_product_id, configLoaded]);

  // =========================================================================
  // OPERATIONS
  // =========================================================================

  const selectOption = useCallback(option_2 => {
    setSelectedOptions(prev_0 => ({
      ...prev_0,
      options: {
        ...prev_0.options,
        [option_2.selection_id]: option_2
      }
    }));
  }, []);
  const selectSwatch = useCallback(swatch_3 => {
    // Add display name with price if needed
    const swatchWithDisplay = {
      ...swatch_3
    };
    if (swatch_3.effective_price > 0) {
      swatchWithDisplay.display_name = `${swatch_3.swatch_name} <span class="pl-1 text-primary">+ ${toCurrency(swatch_3.effective_price)}</span>`;
    } else {
      swatchWithDisplay.display_name = swatch_3.swatch_name;
    }
    setSelectedOptions(prev_1 => ({
      ...prev_1,
      swatches: {
        ...prev_1.swatches,
        [swatch_3.swatch_set_id]: swatchWithDisplay
      }
    }));
  }, []);
  const resetSelections = useCallback(() => {
    setSelectedOptions({
      options: {},
      swatches: {}
    });
    setCurrentVariant(null);
  }, []);

  // =========================================================================
  // RETURN
  // =========================================================================

  return {
    // The product
    product: initialProduct,
    // Selection state
    selectedOptions,
    isComplete,
    selectionErrors,
    swatchSetErrors,
    configLoaded,
    // Variant
    currentVariant,
    optionCode,
    displaySku,
    // Availability
    availability,
    // Retail pricing
    ...pricing,
    // Trade pricing
    ...tradePricingDerived,
    // Lead time
    leadTime,
    optionMessages: null,
    // handled by availability.messages

    // Shipping
    shippingMessage,
    // Images
    images,
    // Operations
    selectOption,
    selectSwatch,
    resetSelections
  };
}
export default useProduct;